import {
    Avatar,
    Button,
    Copy,
    Dialog,
    dispatchCustomEvent,
    FILockClosed,
    Flexer,
    Form,
    FormControl,
    FormHelperText,
    FormLabel,
    Heading,
    Icon,
    IconLib,
    If,
    Input,
    InputControl,
    InputPrefix,
    InputSuffix,
    Label,
    logger,
    Modal,
    ModalClose,
    Notification,
    NotificationContent,
    NotificationIcon,
    Option,
    Options,
    plural,
    Popover,
    Portal,
    Select,
    SelectOption,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Toggle,
    useDialog,
    useInput,
    UserList,
    UserListUser,
    useToast,
    useToggle,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    TbCircleMinus,
    TbCircleMinus2,
    TbExclamationCircle,
    TbMinus,
    TbSpeakerphone,
    TbSquareRoundedMinus,
    TbTrash,
    TbUserPlus,
} from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block, ColorButton, Error, IconPanel, Loading, Tiptap, UserSelectUser } from '../components'
import { COLORS } from '../constants'
import { byName, getMentionsfromTipTapJSON, icons, urlContainsId } from '../helpers'
import { useProjects, useProjectUsers, useTeamUsers, useWorkspaceUsers } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import { useFile } from '../hooks/file.hook'

export const ProjectModal = ({ projectId, onDismiss, onDelete, defaultTab = 0 }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const { workspaceId } = useParams()
    const tools = useVisibility()
    const { setDialog, closeDialog } = useDialog()
    const [color, setColor] = useState('')
    const [team, setTeam] = useState<any>({})
    const name = useInput('')
    const [description, setDescription] = useState('')
    const [roadmapDescription, setRoadmapDescription] = useState('')
    const [shortcode, setShortcode] = useState('')
    const [icon, setIcon] = useState('')
    const [project, setProject] = useState(null)
    const isClosed = useToggle()
    const isChannel = useToggle()
    const joinable = useToggle()
    const roadmap = useToggle()
    const projectUser = useRef(null)
    const deleteDialog = useVisibility(false)
    const workspaceUsersAPI = useWorkspaceUsers()
    const dangerZone = useToggle(false)
    const [tabSelected, setTabSelected] = useState(defaultTab)
    const [users, setUsers] = useState<any>([])
    const [roadmapType, setRoadmapType] = useState('board')
    const [id, setId] = useState(null)
    const [deleteTeamMemberId, setDeleteTeamMemberId] = useState(null)
    const projectUsersAPI = useProjectUsers()
    const [isPrivate, setIsPrivate] = useState(false)
    const [roadmapImage, setRoadmapImage] = useState(null)
    const [image, setImage] = useState(null)
    const fileRef = useRef<any>(null)
    const teamUsersAPI = useTeamUsers()
    const projectsAPI = useProjects()
    const navigate = useNavigate()
    const { showToast } = useToast()
    const [pageUsers, setPageUsers] = useState(1)
    const [filter, setFilter] = useState('')
    const actions = useAppStore((state) => state.actions)
    const currentProject = useAppStore((state) => state.project)
    const workspace = useAppStore((state) => state.workspace)
    const { upload, getStorableUrl } = useFile()
    //const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const isAdmin = useMemo(() => workspace.role != 'Guest', [workspace.role])
    const projectTypeText = useMemo(() => (id ? (isChannel.on ? 'Channel' : 'Project') : ''), [isChannel.on, id])
    const filteredUsers = useMemo(
        () => users.filter((user) => user.name.toLowerCase().includes(filter.toLowerCase())),
        [users, filter]
    )
    const [availableUsers, setAvailableUsers] = useState([])
    const selected = useMemo(() => users.map(({ id }) => id), [users.length])
    const options: any = useMemo(() => {
        return availableUsers.map((user: UserSelectUser) => ({
            key: user.id,
            label: user.name,
            prefix: (
                <Avatar
                    src={user.image}
                    name={user.name}
                    size="xs"
                />
            ),
        }))
    }, [availableUsers])

    const handleFileChange = async (e) => {
        if (e.target.files.length == 0) return

        setLoading(true)
        setError(null)

        try {
            const { url } = await upload({
                folder: workspaceId,
                file: e.target.files[0],
                secure: false,
            })

            setRoadmapImage(url)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleUserSelect = (option: SelectOption) => {
        const availableUser = availableUsers.find((availableUser: any) => availableUser.id == option.key)
        const user = users.find((user: any) => user.id == option.key)

        if (!user) {
            handleUserAdd(availableUser)
        } else {
            handleUserDelete(user)
        }
    }

    const handleUserFilter = async (query) => {
        setError(null)

        try {
            const users = await workspaceUsersAPI.getSearch(workspaceId, query)
            setAvailableUsers(users.sort(byName))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleUserAdd = async (user) => {
        const projectUser = await projectUsersAPI.create(workspaceId, projectId, { users: [user] })
        const { id } = projectUser[0]
        setUsers([...users, { ...user, projectUserId: id }])
        dispatchCustomEvent('project:refresh-preview', { id: projectId })
    }

    const handleUserDelete = async (user) => {
        const { id, projectUserId } = user
        setUsers(users.filter((u) => u.id != id))
        await projectUsersAPI.remove(workspaceId, projectId, projectUserId)
        dispatchCustomEvent('project:refresh-preview', { id: projectId })
    }

    const handleRemoveProjectMember = async (projectUserId) => {
        setDialog({
            title: 'Are you sure?',
            description:
                'They will lose access to this ' + projectTypeText.toLocaleLowerCase() + ' and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            projectUsersAPI
                                .remove(workspaceId, projectId, projectUserId)
                                .then(() => {
                                    fetchData()
                                    dispatchCustomEvent('project:refresh-preview', { id: projectId })
                                    closeDialog()
                                })
                                .catch(() => {
                                    closeDialog()
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleLeave = async () => {
        setDialog({
            title: 'Are you sure?',
            description:
                'You will lose access to this ' + projectTypeText.toLocaleLowerCase() + ' and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.projectDelete(projectId)
                            actions.starredDelete(projectId)
                            closeDialog()
                            onDismiss()
                            projectUsersAPI.remove(workspaceId, projectId, projectUser.current.id)
                            urlContainsId(projectId, () => navigate(`/app/workspace/${workspaceId}/today`))
                        }}
                        variant="danger">
                        Leave
                    </Button>
                </View>
            ),
        })
    }

    const handleDelete = async () => {
        setDialog({
            title: 'Delete this ' + projectTypeText.toLocaleLowerCase() + '?',
            description: 'Are you sure this is correct?',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.projectDelete(projectId)
                            actions.starredDelete(projectId)
                            closeDialog()
                            onDismiss()
                            projectsAPI.remove(workspaceId, projectId)
                            urlContainsId(projectId, () => navigate(`/app/workspace/${workspaceId}/today`))
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleInviteToggle = async (e) => {
        const project = { id: projectId, isClosed: !isClosed.on }
        isClosed.toggle()
        actions.projectUpdate(project)
        await projectsAPI.update(workspaceId, projectId, project)
    }

    const handleJoinableToggle = async (e) => {
        const project = { id: projectId, joinable: !joinable.on }
        joinable.toggle()
        actions.projectUpdate(project)
        await projectsAPI.update(workspaceId, projectId, project)
    }

    const handleRoadmapDescriptionSave = async (html, json, editor) => {
        const description = html
        const mentions = getMentionsfromTipTapJSON(json)
        setRoadmapDescription(description)
    }

    const handleDescriptionSave = async (html, json, editor) => {
        const description = html
        const mentions = getMentionsfromTipTapJSON(json)
        setDescription(description)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setError(null)

        try {
            const project = {
                id: projectId,
                name: name.value,
                image: image ? getStorableUrl(image) : null,
                roadmapImage: roadmapImage ? getStorableUrl(roadmapImage) : null,
                description,
                roadmapDescription,
                roadmapType,
                icon: icon,
                color: color,
                isClosed: isClosed.on,
                isChannel: isChannel.on,
                joinable: joinable.on,
                roadmap: roadmap.on,
            }
            actions.projectUpdate(project)
            showToast({ message: projectTypeText + ' successfully updated.', anchor: 'bottom-right' })
            await projectsAPI.update(workspaceId, projectId, project)
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const project = await projectsAPI.getOne(workspaceId, projectId)
            const team = { name: project.teamName, id: project.teamId }
            const users = !!team.id
                ? await teamUsersAPI.getAll(workspaceId, team.id)
                : await projectUsersAPI.getAll(workspaceId, projectId)

            name.setValue(project.name)

            projectUser.current = { id: project.projectUserId, role: project.role }

            if (project.isClosed) isClosed.turnOn()
            if (project.isChannel) isChannel.turnOn()
            if (project.joinable) joinable.turnOn()
            if (project.roadmap) roadmap.turnOn()

            setProject(project)
            setId(project.id)
            setIsPrivate(project.isPrivate)
            setDescription(project.description)
            setRoadmapDescription(project.roadmapDescription)
            setRoadmapImage(project.roadmapImage)
            setRoadmapType(project.roadmapType)
            setImage(project.image)
            setIcon(project.icon)
            setShortcode(project.shortcode)
            setColor(project.color)
            setTeam(team)
            setUsers(users)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        if (workspaceId && projectId) fetchData()
    }, [workspaceId, projectId])

    return (
        <>
            <Modal
                disableBackgroundDismiss
                width={900}
                height={700}
                anchor="middle-center"
                onDismiss={onDismiss}
                isVisible={true}
                style={{ '--f-modal-body-padding': 0 }}
                header={
                    <>
                        <Heading as="h4">Settings</Heading>
                        <Flexer />
                        <ModalClose onClick={onDismiss} />
                    </>
                }
                footer={
                    <Stack
                        p={10}
                        spacing={10}
                        width="100%">
                        <Button onClick={onDismiss}>Close</Button>
                        {!team.id && (
                            <Button
                                subtle
                                onClick={handleLeave}>
                                Leave {projectTypeText}
                            </Button>
                        )}
                        <Flexer />
                        <Button
                            variant="danger"
                            outline
                            disabled={!isAdmin}
                            onClick={handleDelete}
                            prefix={<Icon icon={TbTrash} />}>
                            Delete {projectTypeText}
                        </Button>
                    </Stack>
                }>
                <Error error={error} />
                <Loading loading={loading} />
                <Tabs
                    selected={tabSelected}
                    onSelect={setTabSelected}
                    layout="left">
                    <TabList width={200}>
                        <Tab>General</Tab>
                        <Tab disabled={!isAdmin}>Joinable</Tab>
                        <Tab disabled={!isAdmin}>Members</Tab>
                        <Tab disabled={!isAdmin}>Roadmap</Tab>
                        <Tab display={isChannel.on ? 'none' : undefined}>Email</Tab>
                        <Tab disabled={!isAdmin}>Feedback Widget</Tab>
                        <Tab disabled={!isAdmin}>Danger Zone</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel>
                            <Form
                                onSubmit={handleSubmit}
                                id="project-modal-form">
                                <View
                                    row
                                    width="100%"
                                    gap="1rem"
                                    alignItems="flex-start">
                                    {!project?.isChannel && (
                                        <Popover
                                            arrow
                                            focusTrap
                                            width={250}
                                            portal={Portal}
                                            isVisible={tools.visible}
                                            onDismiss={tools.hide}
                                            content={
                                                <IconPanel
                                                    icon={icon}
                                                    icons={icons}
                                                    onChange={setIcon}
                                                />
                                            }>
                                            <View
                                                width="fit-content"
                                                //onClick={() => isAdmin && tools.show()}
                                                className="f-buttonize">
                                                <Block
                                                    //icon={icon}
                                                    name={name.value}
                                                    color={color}
                                                    size="2xl"
                                                />
                                            </View>
                                        </Popover>
                                    )}
                                    <View
                                        column
                                        style={{ overflow: 'hidden' }}
                                        flex={1}
                                        p="0 0.5rem">
                                        <Stack
                                            width="100%"
                                            direction="vertical"
                                            spacing="1rem">
                                            {!isPrivate && (
                                                <>
                                                    <Label>{projectTypeText} name</Label>
                                                    <InputControl>
                                                        <InputPrefix>
                                                            <ColorButton
                                                                color={color}
                                                                onChange={setColor}
                                                                colors={COLORS}
                                                            />
                                                        </InputPrefix>
                                                        <Input
                                                            placeholder="Website project"
                                                            selectOnFocus
                                                            type="text"
                                                            value={name.value}
                                                            onChange={name.onChange}
                                                        />
                                                        {isClosed.on && (
                                                            <InputSuffix>
                                                                <Icon icon={FILockClosed} />
                                                            </InputSuffix>
                                                        )}
                                                    </InputControl>
                                                </>
                                            )}

                                            <FormControl>
                                                <Label
                                                    row
                                                    gap={5}
                                                    justifyContent="flex-start">
                                                    {projectTypeText} description
                                                </Label>
                                                <div className="f-input-control modal__description">
                                                    <Tiptap
                                                        content={description}
                                                        onUpdate={handleDescriptionSave}
                                                        workspaceId={workspaceId}
                                                        projectId={projectId}
                                                        teamId={team.id}
                                                        hideToolbar
                                                    />
                                                </div>
                                                <FormHelperText kind="info">
                                                    This description will show on your {projectTypeText.toLowerCase()}{' '}
                                                    and join page.
                                                </FormHelperText>

                                                {/*         
                                                    <View
                                                        row
                                                        gap="0.5rem"
                                                        justifyContent="flex-start">
                                                        <Toggle
                                                            id="closed"
                                                            disabled={!isAdmin}
                                                            on={!isChannel.on}
                                                            onChange={isChannel.toggle}
                                                        />
                                                        <Label htmlFor="closed">
                                                            This is a {projectTypeText.toLowerCase()}
                                                        </Label>
                                                    </View> 
                                                */}
                                            </FormControl>
                                            <Button
                                                type="submit"
                                                variant="accent"
                                                disabled={!name.value || !isAdmin}
                                                form="project-modal-form">
                                                Save
                                            </Button>
                                        </Stack>
                                    </View>
                                </View>
                            </Form>
                        </TabPanel>

                        <TabPanel>
                            <Stack
                                spacing="1rem"
                                direction="vertical">
                                <Text colorToken="text-weaker">
                                    Joinable projects are projects or channels that can be joined by an external person
                                    to your workspace. External guest users are not counted toward your workspace member
                                    limit.
                                </Text>
                                <View
                                    row
                                    gap="0.5rem"
                                    justifyContent="flex-start">
                                    <Toggle
                                        id="closed"
                                        disabled={!isAdmin}
                                        on={joinable.on}
                                        onChange={handleJoinableToggle}
                                    />
                                    <Label htmlFor="closed">Joinable</Label>
                                </View>
                                {!isPrivate && joinable.on && (
                                    <FormControl>
                                        <FormLabel>Guest join link:</FormLabel>
                                        <Copy
                                            label={`${window.location.origin}/c/${shortcode}`}
                                            value={`${window.location.origin}/c/${shortcode}`}
                                        />
                                    </FormControl>
                                )}
                            </Stack>
                        </TabPanel>

                        <TabPanel>
                            <View
                                zIndex={0}
                                width="100%"
                                height="100%"
                                position="absolute"
                                style={{ inset: 0 }}
                                p="var(--f-space-3)"
                                className="f-overflow-y-auto">
                                <Stack
                                    direction="vertical"
                                    spacing="0.75rem">
                                    {!team.id && isClosed.on && (
                                        <Notification 
                                            radius="var(--f-radius)"
                                            variant="danger">
                                            <NotificationContent>
                                                This is a private {projectTypeText.toLowerCase()}, only members have
                                                access.
                                            </NotificationContent>
                                        </Notification>
                                    )}

                                    {!!team.id && (
                                        <Notification 
                                            radius="var(--f-radius)"
                                            variant="danger">
                                            <NotificationContent>
                                                {projectTypeText} members are managed in the {team.name} team.
                                            </NotificationContent>
                                        </Notification>
                                    )}

                                    <View
                                        row
                                        gap="0.5rem">
                                        <Label
                                            m="0 auto 0 0"
                                            fontWeight={600}
                                            size="sm">
                                            {users.length} {plural(users.length, ' member')}
                                        </Label>
                                        {!team.id && !isPrivate && (
                                            <>
                                                <Label htmlFor="closed">
                                                    This is an invite-only {projectTypeText.toLocaleLowerCase()}
                                                </Label>
                                                <Toggle
                                                    id="closed"
                                                    disabled={!isAdmin}
                                                    on={isClosed.on}
                                                    onChange={handleInviteToggle}
                                                />
                                            </>
                                        )}
                                    </View>

                                    {!team.id && (
                                        <Select
                                            prefix={<Icon icon={TbUserPlus} />}
                                            width="100%"
                                            noListFocus
                                            trapFocus
                                            selected={selected}
                                            disabled={!isAdmin}
                                            options={options}
                                            placeholder="User..."
                                            customPlaceholder="Add user..."
                                            filterDelay={1000}
                                            selectListProps={{
                                                noOptionsComponent: (
                                                    <Text p="var(--f-select-option-padding)">There are no users.</Text>
                                                ),
                                            }}
                                            onFilter={handleUserFilter}
                                            onSelect={(option, dismiss, clear) => {
                                                clear()
                                                handleUserSelect(option)
                                            }}
                                        />
                                    )}

                                    <UserList
                                        shadow="none"
                                        users={filteredUsers.map(
                                            (user: any): UserListUser => ({
                                                id: user.id,
                                                name: user.name,
                                                description: user.title,
                                                image: user.image,
                                                tools: (
                                                    <If if={!team.id && isAdmin}>
                                                        <Button
                                                            subtle
                                                            size="sm"
                                                            onClick={() =>
                                                                handleRemoveProjectMember(user.projectUserId)
                                                            }>
                                                            <Icon icon={TbCircleMinus} />
                                                        </Button>
                                                    </If>
                                                ),
                                            })
                                        )}
                                    />

                                    {!filteredUsers.length && <Text p="2rem 0">No users found</Text>}

                                    {/*           
                                    {!!filteredUsers.length && (
                                        <View
                                            row
                                            width="100%"
                                            m="0 auto"
                                            p="1rem 0 0 0">
                                            <Pagination
                                                width={150}
                                                pageSize={5}
                                                totalCount={filteredUsers.length}
                                                currentPage={pageUsers}
                                                siblingCount={4}
                                                separator="..."
                                                onPageChange={(page) => setPageUsers(page)}
                                            />
                                        </View>
                                    )} 
                                     */}
                                </Stack>
                            </View>
                        </TabPanel>

                        <TabPanel>
                            <Form
                                onSubmit={handleSubmit}
                                id="project-modal-form">
                                <View
                                    row
                                    alignItems="flex-start"
                                    style={{ overflow: 'hidden' }}
                                    flex={1}
                                    gap="1rem"
                                    p="0 0.5rem">
                                    <View>
                                        <input
                                            accept="image/png,image/jpg"
                                            type="file"
                                            className="hide"
                                            ref={fileRef}
                                            onChange={handleFileChange}
                                        />

                                        <Avatar
                                            key={roadmapImage ?? 'workspace-modal-avatar'}
                                            src={roadmapImage}
                                            name={name.value}
                                            size="xl"
                                            width={150}
                                            height={150}
                                            className="f-buttonize"
                                            onClick={handleFileClick}
                                        />

                                        <Button
                                            m="0.5rem 0 0 0"
                                            disabled={!isAdmin}
                                            prefix={<IconLib icon="paperclip" />}
                                            onClick={handleFileClick}>
                                            Change image
                                        </Button>
                                    </View>
                                    <Stack
                                        width="100%"
                                        direction="vertical"
                                        spacing="1rem">
                                        <FormControl>
                                            <FormLabel
                                                row
                                                gap={5}
                                                justifyContent="flex-start">
                                                Roadmap description
                                            </FormLabel>
                                            <div className="f-input-control modal__description">
                                                <Tiptap
                                                    content={roadmapDescription}
                                                    onUpdate={handleRoadmapDescriptionSave}
                                                    workspaceId={workspaceId}
                                                    projectId={projectId}
                                                    teamId={team.id}
                                                    hideToolbar
                                                />
                                            </div>
                                            <FormHelperText kind="info">
                                                This description will show on your roadmap page.
                                            </FormHelperText>
                                            <View
                                                row
                                                gap="0.5rem"
                                                justifyContent="flex-start">
                                                <Toggle
                                                    id="closed"
                                                    disabled={!isAdmin}
                                                    on={roadmap.on}
                                                    onChange={roadmap.toggle}
                                                />
                                                <Label htmlFor="closed">Roadmap</Label>
                                            </View>
                                        </FormControl>
                                        {roadmap.on && (
                                            <>
                                                <FormControl>
                                                    <FormLabel>Roadmap link:</FormLabel>
                                                    <Copy
                                                        label={`${window.location.origin}/roadmap/${shortcode}`}
                                                        value={`${window.location.origin}/roadmap/${shortcode}`}
                                                    />
                                                </FormControl>
                                                {/*  <FormControl>
                                                    <FormLabel>Roadmap type:</FormLabel>
                                                    <Options
                                                        animated
                                                        selected={roadmapType == 'board' || !roadmapType ? 0 : 1}
                                                        onOptionChange={(index) =>
                                                            index == 0
                                                                ? setRoadmapType('board')
                                                                : setRoadmapType('list')
                                                        }>
                                                        <Option>Board</Option>
                                                        <Option>List</Option>
                                                    </Options>
                                                </FormControl> */}
                                            </>
                                        )}
                                        <Button
                                            type="submit"
                                            variant="accent"
                                            form="project-modal-form">
                                            Save
                                        </Button>
                                    </Stack>
                                </View>
                            </Form>
                        </TabPanel>

                        <TabPanel
                            display={isChannel.on ? 'none' : undefined}
                            p="var(--f-space-3)">
                            <FormControl>
                                <FormLabel htmlFor="webhook">Email address:</FormLabel>
                                <Copy
                                    id="webhook"
                                    value={`${projectId}@inbound.fronteer.app`}
                                />
                                <FormHelperText kind="info">
                                    Create project tasks by sending email directly to this email address.
                                </FormHelperText>
                            </FormControl>
                        </TabPanel>

                        <TabPanel
                            display={isChannel.on ? 'none' : undefined}
                            p="var(--f-space-3)">
                            <Stack
                                spacing="1rem"
                                direction="vertical">
                                <Notification
                                    variant="highlight"
                                    width="100%">
                                    <NotificationIcon>
                                        <Icon icon={TbExclamationCircle} />
                                    </NotificationIcon>
                                    <NotificationContent>
                                        This feature is in beta - we are working actively in improving documentation &
                                        functionality.
                                    </NotificationContent>
                                </Notification>
                                <Heading as="h3">Feedback Widget</Heading>
                                <Text>
                                    Embed an easy to use feedback widget to collect feedback straight in the{' '}
                                    <strong>{project?.name}</strong> project.
                                </Text>
                                <Label>Copy & paste the following snippet into the head of your HTML page.</Label>
                                <Copy
                                    size="sm"
                                    value='&lt;script src="https://js.fronteer.app/v1.js" defer&gt;&lt;/script&gt;'
                                />
                                <Label>Open the feedback widget using the following button logic:</Label>
                                <Copy
                                    size="sm"
                                    value={`<button
                                            onclick="fronteer(this)"
                                            data-shortcode="${shortcode}"
                                            data-theme="light">
                                            Give Feedback
                                        </button>`}
                                />
                                <Label>Or with React:</Label>
                                <Copy
                                    size="sm"
                                    value={`<Button
                                            onClick={(e) => window['fronteer'](e.target)}
                                            data-shortcode="${shortcode}"
                                            data-theme="light">
                                            Give Feedback
                                        </Button>`}
                                />
                                <Label>Shortcode:</Label>
                                <Copy
                                    size="sm"
                                    value={shortcode}
                                />
                                <Button
                                    onClick={(e) => window['fronteer'](e.target)}
                                    data-shortcode="7c3ffd37-5b5e-4d1e-abe8-cef148332661"
                                    data-theme="light"
                                    variant="accent"
                                    prefix={<Icon icon={TbSpeakerphone} />}>
                                    Test it here
                                </Button>
                            </Stack>
                        </TabPanel>

                        <TabPanel>
                            <Stack
                                direction="vertical"
                                spacing={15}>
                                <Text colorToken="text-weaker">
                                    Deleting a {projectTypeText.toLowerCase()} is permanent & non recoverable. Before
                                    deleting it, perhaps create an archive group to hide it from view.
                                </Text>
                                <Button
                                    variant="danger"
                                    onClick={deleteDialog.show}
                                    prefix={<Icon icon={TbTrash} />}>
                                    Delete {projectTypeText}
                                </Button>
                            </Stack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Modal>

            <Dialog
                closeButton
                isVisible={deleteDialog.visible}
                title="Delete this project?"
                description="All tasks, messages & files will be lost. Are you sure this is correct?"
                onDismiss={deleteDialog.hide}
                footer={
                    <>
                        <Button onClick={deleteDialog.hide}>Cancel</Button>
                        <Flexer />
                        <Button
                            onClick={onDelete}
                            variant="danger"
                            outline>
                            Delete
                        </Button>
                    </>
                }
            />
        </>
    )
}
